import type { BusinessType } from "@zenith/features/AuthZ/permissions";

export enum TeamMemberRole {
  ADMIN = "ADMIN",
  MANAGER = "MANAGER",
  ACCOUNTANT = "ACCOUNTANT",
  CASHIER = "CASHIER",
}

export type RoleDefinition = {
  id: TeamMemberRole;
  label: string;
};

// Admin's bullets differ by business type: a Multi-signatory/corporate Admin
// is a full owner-equivalent with no approval step, while an Individual &
// Sole Proprietorship Admin still needs approval to initiate transactions
// (matches ADMIN_MS vs ADMIN_IND below - MS has no "requires approval"
// wording anywhere, IND's Send money/Payments categories do).
const ADMIN_BULLETS: Record<BusinessType, string[]> = {
  MS: [
    "Access to all outlet by default – Not tied to an outlet",
    "Perform all functions as the owner.",
  ],
  IND: [
    "Access to all outlet by default – Not tied to an outlet",
    "Perform all functions as the owner.",
    "Initiate transactions but requires approval.",
  ],
};

const MANAGER_BULLETS = [
  "Must be tied to specific outlet",
  "Can perform all functions except initiate fund transfer",
  "Can initiate funds transfer if linked to main wallet but requires approval (Except for individual and sole proprietorship)",
];

const ACCOUNTANT_BULLETS = [
  "Must be tied to specific outlet",
  "Can view transactions",
  "Can export statement",
  "Can initiate funds transfer if linked to main wallet but requires approval (Except for individual and sole proprietorship)",
];

const CASHIER_BULLETS = [
  "Only see transactions performed by them",
  "Can collect payment",
];

export const ROLE_DEFINITIONS: RoleDefinition[] = [
  { id: TeamMemberRole.ADMIN, label: "Admin" },
  { id: TeamMemberRole.MANAGER, label: "Manager" },
  { id: TeamMemberRole.ACCOUNTANT, label: "Accountant" },
  { id: TeamMemberRole.CASHIER, label: "Cashier" },
];

export function getRoleBullets(
  role: TeamMemberRole,
  businessType: BusinessType
): string[] {
  switch (role) {
    case TeamMemberRole.ADMIN:
      return ADMIN_BULLETS[businessType];
    case TeamMemberRole.MANAGER:
      return MANAGER_BULLETS;
    case TeamMemberRole.ACCOUNTANT:
      return ACCOUNTANT_BULLETS;
    case TeamMemberRole.CASHIER:
      return CASHIER_BULLETS;
  }
}

export const ROLE_SELECT_ITEMS = ROLE_DEFINITIONS.map((role) => ({
  value: role.id,
  label: role.label,
}));

export type PermissionCategory = {
  name: string;
  privileges: string[];
};

// The full Admin/Manager/Accountant/Cashier breakdown from BOAS-7641's
// "Multi-signatory Business Roles and Permissions" and "Individual & Sole
// Proprietorship Business Roles and Permissions" tables, transcribed
// verbatim per role per business type - not just the Admin/Home snippet
// visible in the Figma screen. Differences between the two business types:
// Admin's Send money/Payments (IND only) vs Refund/VAAS (MS only); Manager
// drops VAAS under IND; Accountant and Cashier are identical in both.
const ADMIN_MS: PermissionCategory[] = [
  { name: "Home", privileges: ["Can See Balance"] },
  {
    name: "Transactions (All outlets)",
    privileges: ["View transactions", "Can export transactions"],
  },
  {
    name: "Collect Payment",
    privileges: ["Can collect payment via inventory", "Direct payment collection"],
  },
  {
    name: "POS management",
    privileges: ["Can request for POS", "Can manage POS (Return POS…etc)"],
  },
  { name: "Dispute", privileges: ["Can log and manage disputes."] },
  { name: "Refund", privileges: ["Can initiate refund disputes."] },
  {
    name: "Inventory Management",
    privileges: [
      "Can manage inventory (Create Product, manage product)",
      "Can update stock",
    ],
  },
  {
    name: "Analytics",
    privileges: ["Can view reports/analytics", "Undo Sale"],
  },
  {
    name: "Outlet",
    privileges: [
      "Create Outlet",
      "View Outlet information",
      "Manage outlet settings",
      "Withdrawal",
      "Autosweep",
      "Activate/Deactivate outlet",
      "Enable/Disable auto sweep",
      "Withdrawal History",
      "POS",
    ],
  },
  {
    name: "VAAS",
    privileges: [
      "Generate Account",
      "View Accounts",
      "Activate and deactivate account",
      "View transaction history",
    ],
  },
  {
    name: "Settings",
    privileges: [
      "Update profile information",
      "Manage preferences",
      "Generate statement",
      "Create and Manage team members",
    ],
  },
];

const ADMIN_IND: PermissionCategory[] = [
  { name: "Home", privileges: ["Can See Balance"] },
  {
    name: "Transactions (All outlets)",
    privileges: ["View transactions", "Can export transactions"],
  },
  {
    name: "Collect Payment",
    privileges: ["Can collect payment via inventory", "Direct payment collection"],
  },
  {
    name: "Send money",
    privileges: [
      "Can initiate send money but requires approval. (All send money and transactions available modules available)",
    ],
  },
  {
    name: "Payments",
    privileges: ["Can initiate bills payment but require approval."],
  },
  {
    name: "POS management",
    privileges: [
      "Can request for POS (requires approval)",
      "Can manage POS (Return POS…etc)",
    ],
  },
  { name: "Dispute", privileges: ["Can log and manage disputes."] },
  {
    name: "Inventory Management",
    privileges: [
      "Can manage inventory (Create Product, delete, edit product, update product information)",
      "Can update stock",
    ],
  },
  { name: "Analytics", privileges: ["Can view reports/analytics"] },
  {
    name: "Outlet",
    privileges: [
      "Create Outlet",
      "View Outlet information",
      "Manage outlet settings",
      "Withdrawal",
      "Autosweep",
      "Activate/Deactivate outlet",
      "Enable/Disable auto sweep",
      "Withdrawal History",
      "POS",
    ],
  },
  {
    name: "Settings",
    privileges: [
      "Update profile information",
      "Manage preferences",
      "Generate statement",
      "Create and Manage team members",
    ],
  },
];

const MANAGER_OUTLET_PRIVILEGES = [
  "View Outlet information",
  "Manage outlet settings",
  "Withdrawal",
  "Autosweep",
  "Activate/Deactivate outlet",
  "Enable/Disable auto sweep",
  "Withdrawal History",
  "POS",
];

const MANAGER_SETTINGS_PRIVILEGES = [
  "Update profile information",
  "Generate statement",
  "Create and Manage team members – (All other roles except admin) only to the specific outlets they have access to.",
];

const MANAGER_MS: PermissionCategory[] = [
  {
    name: "Home",
    privileges: ["Can See Balance for the specific outlet linked to."],
  },
  {
    name: "Transactions (Only specific outlet linked to)",
    privileges: ["View transactions", "Can export transactions"],
  },
  {
    name: "Dispute (Only specific outlets linked to)",
    privileges: ["Can log and manage disputes."],
  },
  {
    name: "Inventory Management (Only specific outlets linked to)",
    privileges: ["Can update inventory stock."],
  },
  {
    name: "Analytics (Only specific outlets linked to)",
    privileges: ["Can view reports/analytics", "Undo Sale"],
  },
  {
    name: "Outlet (Only specific outlets linked to)",
    privileges: MANAGER_OUTLET_PRIVILEGES,
  },
  {
    name: "VAAS",
    privileges: [
      "Generate Account",
      "View Accounts",
      "Activate and deactivate account",
      "View transaction history",
    ],
  },
  {
    name: "Settings (Only specific outlets linked to)",
    privileges: MANAGER_SETTINGS_PRIVILEGES,
  },
];

// Same as Manager/MS, minus VAAS - that category doesn't exist for
// Individual & Sole Proprietorship businesses (per permissions.ts's
// "vaas:manage": { IND: {} }).
const MANAGER_IND: PermissionCategory[] = MANAGER_MS.filter(
  (category) => category.name !== "VAAS"
);

// Accountant and Cashier breakdowns are identical in both business types.
const ACCOUNTANT: PermissionCategory[] = [
  {
    name: "Home",
    privileges: ["Can See Balance for the specific outlet linked to."],
  },
  {
    name: "Transactions (Only specific outlet linked to)",
    privileges: ["View transactions", "Can export transactions"],
  },
  {
    name: "Analytics (Only specific outlets linked to)",
    privileges: ["Can view reports/analytics"],
  },
  {
    name: "Settings (Only specific outlets linked to)",
    privileges: ["Update profile information", "Generate statement"],
  },
];

const CASHIER: PermissionCategory[] = [
  { name: "Home", privileges: ["Can See Balance only for money collected."] },
  {
    name: "Collect Payment",
    privileges: ["Can collect payment via inventory", "Direct payment collection"],
  },
  {
    name: "Transactions (Only for transactions collected)",
    privileges: ["View transactions", "Can export transactions"],
  },
  {
    name: "Analytics (Only for transactions collected)",
    privileges: ["Can view reports/analytics"],
  },
  {
    name: "Settings (Only specific outlets linked to)",
    privileges: ["Update profile information", "Generate statement"],
  },
];

export function getPermissionCategories(
  role: TeamMemberRole,
  businessType: BusinessType
): PermissionCategory[] {
  switch (role) {
    case TeamMemberRole.ADMIN:
      return businessType === "IND" ? ADMIN_IND : ADMIN_MS;
    case TeamMemberRole.MANAGER:
      return businessType === "IND" ? MANAGER_IND : MANAGER_MS;
    case TeamMemberRole.ACCOUNTANT:
      return ACCOUNTANT;
    case TeamMemberRole.CASHIER:
      return CASHIER;
  }
}
